import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { api, type Session } from '../services/api'
import { formatDistanceToNow } from 'date-fns'
import { useEffect, useState, useMemo } from 'react'
import { useAnalyticsPeriod } from '../lib/analytics-period'

type SortField = 'user_name' | 'title' | 'created_at' | 'updated_at' | 'message_count'
type SortDirection = 'asc' | 'desc'

export default function Sessions() {
  const { period } = useAnalyticsPeriod()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [showEmptySessions, setShowEmptySessions] = useState(false)

  useEffect(() => setCurrentPage(1), [period])

  const { data: sessions, isLoading, error, refetch } = useQuery({
    queryKey: ['sessions', period],
    queryFn: () => api.getSessions(period),
    staleTime: 30000,
    gcTime: 0,
  })

  const handleSessionClick = (session: Session) => {
    navigate(`/sessions/${session.session_id}`)
  }

  // Filter and sort
  const filteredAndSortedSessions = useMemo(() => {
    if (!sessions) return []

    // Filter
    const searchLower = search.toLowerCase()
    let filtered = sessions.filter((session) => {
      const matchesSearch = !searchLower || (
        session.user_name?.toLowerCase().includes(searchLower) ||
        session.user_email?.toLowerCase().includes(searchLower) ||
        session.title?.toLowerCase().includes(searchLower) ||
        session.session_id.toLowerCase().includes(searchLower)
      )

      // When searching, always show matching sessions regardless of empty filter
      if (searchLower) return matchesSearch

      // No search: apply the empty sessions toggle
      if (!showEmptySessions && (!session.message_count || session.message_count === 0)) {
        return false
      }

      return matchesSearch
    })

    // Sort
    filtered.sort((a, b) => {
      let aVal: any = a[sortField]
      let bVal: any = b[sortField]

      if (aVal === null || aVal === undefined) return 1
      if (bVal === null || bVal === undefined) return -1

      if (sortField === 'created_at' || sortField === 'updated_at') {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [sessions, search, sortField, sortDirection, showEmptySessions])

  // Paginate
  const totalPages = Math.ceil(filteredAndSortedSessions.length / pageSize)
  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredAndSortedSessions.slice(start, start + pageSize)
  }, [filteredAndSortedSessions, currentPage, pageSize])

  const handleSearch = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="opacity-30">⬍</span>
    return sortDirection === 'asc' ? <span>▲</span> : <span>▼</span>
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <h1 className="page-title mb-6">Session Management</h1>
        <div className="flex justify-center items-center h-64">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="card bg-base-100 shadow-xl max-w-md border border-base-300">
          <div className="card-body items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-error mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="card-title text-base-content">Failed to load sessions</h2>
            <p className="text-base-content opacity-80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <div className="card-actions mt-4">
              <button className="btn btn-primary" onClick={() => refetch()}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="page-title">Session Management</h1>
        <div className="flex gap-2">
          <div className="badge badge-lg badge-primary">
            {filteredAndSortedSessions.length} Active Sessions
          </div>
          {!showEmptySessions && sessions && (
            <div className="badge badge-lg badge-ghost">
              {sessions.filter(s => !s.message_count || s.message_count === 0).length} Empty Hidden
            </div>
          )}
        </div>
      </div>

      {/* Search and Controls */}
      <div className="flex gap-4 mb-4">
        <input
          type="text"
          placeholder="Search by user, title, or session ID..."
          className="input input-bordered flex-1"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <label className="label cursor-pointer gap-2 border rounded-lg px-4 border-base-300 bg-base-100">
          <span className="label-text whitespace-nowrap">Show empty</span>
          <input
            type="checkbox"
            className="toggle toggle-primary"
            checked={showEmptySessions}
            onChange={(e) => {
              setShowEmptySessions(e.target.checked)
              setCurrentPage(1)
            }}
          />
        </label>
        <select
          className="select select-bordered w-32"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value))
            setCurrentPage(1)
          }}
        >
          <option value={10}>10 / page</option>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
      </div>

      <div className="card bg-base-200 shadow-xl">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th onClick={() => handleSort('user_name')} className="cursor-pointer select-none hover:bg-base-300">
                    User <SortIcon field="user_name" />
                  </th>
                  <th onClick={() => handleSort('title')} className="cursor-pointer select-none hover:bg-base-300">
                    Title <SortIcon field="title" />
                  </th>
                  <th onClick={() => handleSort('created_at')} className="cursor-pointer select-none hover:bg-base-300">
                    Created <SortIcon field="created_at" />
                  </th>
                  <th onClick={() => handleSort('updated_at')} className="cursor-pointer select-none hover:bg-base-300">
                    Last Updated <SortIcon field="updated_at" />
                  </th>
                  <th onClick={() => handleSort('message_count')} className="cursor-pointer select-none hover:bg-base-300">
                    Messages <SortIcon field="message_count" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedSessions.map((session: Session) => (
                  <tr
                    key={session.session_id}
                    className="hover cursor-pointer"
                    onClick={() => handleSessionClick(session)}
                  >
                    <td>
                      <div className="font-medium">{session.user_name || 'Unknown'}</div>
                      <div className="text-sm opacity-70">{session.user_email || '—'}</div>
                    </td>
                    <td className="max-w-md truncate">{session.title || 'Untitled Session'}</td>
                    <td>{formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}</td>
                    <td>{formatDistanceToNow(new Date(session.updated_at), { addSuffix: true })}</td>
                    <td>
                      <div className="badge badge-ghost">{session.message_count || 0}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAndSortedSessions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-base-content/60">
                {search ? 'No sessions match your search' : 'No sessions found'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <div className="text-sm opacity-70">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredAndSortedSessions.length)} of {filteredAndSortedSessions.length}
          </div>

          <div className="flex gap-2">
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <span className="flex items-center px-4 text-sm">
              Page {currentPage} of {totalPages}
            </span>

            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>

          <div className="w-32"></div>
        </div>
      )}
    </div>
  )
}
